import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Truck, Store, Check, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { euro } from "../../utils/format";
import { lookupZone, COLLECT_SLOTS } from "../../data/zones";
import { SITE_IMAGES } from "../../data/siteImages";
import PlantImage from "../../components/fancyplants/PlantImage";
import { EmptyState } from "../../components/fancyplants/states";
import { SITE } from "../../config/site";

const FREE_DELIVERY_THRESHOLD = 60;

export default function CheckoutPage() {
  const { lines, subtotal, clear } = useCart();
  const navigate = useNavigate();

  const [method, setMethod] = useState("delivery"); // delivery | collect
  const [eircode, setEircode] = useState("");
  const [zoneResult, setZoneResult] = useState(null);
  const [slot, setSlot] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  if (lines.length === 0) {
    return (
      <div className="container-fp pt-32 pb-20">
        <EmptyState
          title="Your basket is empty"
          message="Add a plant or two and checkout will spring back to life."
          action={<Link to="/shop" className="btn-forest">Browse the shop</Link>}
        />
      </div>
    );
  }

  const checkZone = () => {
    setError("");
    setZoneResult(lookupZone(eircode));
  };

  const freeDelivery = subtotal >= FREE_DELIVERY_THRESHOLD;
  const deliveryFee =
    method === "collect"
      ? 0
      : freeDelivery
      ? 0
      : zoneResult?.status === "ok"
      ? zoneResult.zone.fee
      : null;

  const total = subtotal + (deliveryFee || 0);

  const canPay =
    name &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    (method === "collect" ? !!slot : zoneResult?.status === "ok");

  // No card payment is wired yet (Stripe to follow). This sends the order request
  // to the shop by email so it's a real, honest enquiry rather than a fake charge.
  const handlePay = async (e) => {
    e.preventDefault();
    setError("");
    if (!canPay) {
      setError("Please complete the highlighted details before sending your request.");
      return;
    }
    setPaying(true);
    const ref = "FP-" + Math.random().toString(36).slice(2, 8).toUpperCase();
    const detail =
      method === "collect"
        ? `Collect: ${slot}`
        : `Delivery to ${zoneResult.zone.name} · ${zoneResult.zone.eta}`;
    const itemsText = lines
      .map((l) => `- ${l.qty}x ${l.name} — ${euro(l.price * l.qty)}`)
      .join("\n");
    const message =
      `New order request ${ref}\n\n${itemsText}\n\n` +
      `Subtotal: ${euro(subtotal)}\n` +
      `${method === "collect" ? "Collection" : "Delivery"}: ${
        deliveryFee == null ? "TBC" : deliveryFee === 0 ? "Free" : euro(deliveryFee)
      }\n` +
      `Total: ${euro(total)}\n\nFulfilment: ${detail}\n` +
      (method === "delivery" ? `Eircode: ${eircode}\n` : "");
    try {
      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });
      if (!res.ok) throw new Error("send failed");
      clear();
      navigate(
        `/order-confirmed?ref=${ref}&email=${encodeURIComponent(email)}&detail=${encodeURIComponent(detail)}`
      );
    } catch (err) {
      setPaying(false);
      setError(
        `We couldn't submit your request just now — please email your order to ${SITE.email} and we'll sort it straight away.`
      );
    }
  };

  return (
    <div className="pt-24">
      <div className="container-fp py-6">
        <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-forest-700">
          <ArrowLeft size={16} /> Continue shopping
        </Link>
      </div>

      <div className="container-fp grid gap-10 py-4 lg:grid-cols-[1fr_400px]">
        {/* Left: form */}
        <form onSubmit={handlePay} className="space-y-8">
          <div>
            <h1 className="t-h1 mb-1">Checkout</h1>
            <p className="text-ink-soft">Guest checkout — no account needed.</p>
          </div>

          {/* Contact */}
          <section>
            <h2 className="t-h3 mb-4">Your details</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="fp-input"
                  placeholder="Your name"
                  required
                />
              </Field>
              <Field label="Email (for your confirmation)">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="fp-input"
                  placeholder="you@email.ie"
                  required
                />
              </Field>
            </div>
          </section>

          {/* Fulfilment selector */}
          <section>
            <h2 className="t-h3 mb-4">How would you like it?</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <MethodCard
                active={method === "delivery"}
                icon={Truck}
                image={SITE_IMAGES.delivery}
                title="Delivery"
                text="Across most of Ireland. Free over €60."
                onClick={() => setMethod("delivery")}
              />
              <MethodCard
                active={method === "collect"}
                icon={Store}
                image={SITE_IMAGES.collection}
                title="Click & collect"
                text="Pick a half-hour slot at the glasshouse."
                onClick={() => setMethod("collect")}
              />
            </div>

            {method === "delivery" ? (
              <div className="mt-4 surface overflow-hidden">
                <PlantImage src={SITE_IMAGES.delivery} alt="" ratio="5/2" />
                <div className="p-5">
                  <Field label="Eircode">
                    <div className="flex gap-2">
                      <input
                        value={eircode}
                        onChange={(e) => { setEircode(e.target.value); setZoneResult(null); }}
                        className="fp-input uppercase"
                        placeholder="e.g. R35 F2P8"
                      />
                      <button type="button" onClick={checkZone} className="btn-forest whitespace-nowrap">
                        Check zone
                      </button>
                    </div>
                  </Field>

                {zoneResult?.status === "invalid" && (
                  <p className="mt-3 flex items-center gap-2 text-sm text-danger">
                    <AlertCircle size={16} /> That doesn't look like a valid Eircode. Please check and try again.
                  </p>
                )}
                {zoneResult?.status === "ok" && (
                  <div className="mt-3 rounded-xl bg-forest-100 px-4 py-3 text-sm text-forest-900">
                    <p className="flex items-center gap-2 font-medium">
                      <Check size={16} /> We deliver to {zoneResult.zone.name}.
                    </p>
                    <p className="mt-1">
                      Estimated {zoneResult.zone.eta} ·{" "}
                      {freeDelivery ? "Free delivery (over €60)" : `${euro(zoneResult.zone.fee)} delivery`}.
                      We ship early-week so plants never sit in a depot.
                    </p>
                  </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-4 surface overflow-hidden">
                <PlantImage src={SITE_IMAGES.collection} alt="" ratio="5/2" />
                <div className="p-5">
                  <Field label="Choose a collection slot">
                    <div className="grid gap-2 sm:grid-cols-2">
                      {COLLECT_SLOTS.map((s) => (
                        <button
                          type="button"
                          key={s}
                          onClick={() => setSlot(s)}
                          className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors ${
                            slot === s
                              ? "border-forest-500 bg-forest-100 text-forest-900"
                              : "border-line hover:border-forest-500"
                          }`}
                        >
                          {slot === s && <Check size={14} className="mb-1" />}
                          <div>{s}</div>
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>
            )}
          </section>

          {error && (
            <p className="flex items-center gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">
              <AlertCircle size={16} /> {error}
            </p>
          )}

          <button type="submit" disabled={paying} className="btn-brass w-full text-base">
            {paying ? (
              <><Loader2 size={18} className="animate-spin" /> Sending your request…</>
            ) : (
              <>Request order · {euro(total)}</>
            )}
          </button>
          <p className="text-center text-xs text-ink-soft">
            This sends us your order request — nothing is charged now. We'll email you to confirm
            availability and arrange payment or collection. Card payment online is coming soon.
          </p>
        </form>

        {/* Right: order summary */}
        <aside>
          <div className="surface sticky top-24 overflow-hidden">
            <PlantImage src={SITE_IMAGES.packing} alt="" ratio="5/2" />
            <div className="p-5">
            <h2 className="t-h3 mb-4">Your order</h2>
            <ul className="space-y-3">
              {lines.map((l) => (
                <li key={l.id} className="flex gap-3">
                  <div className="w-14 shrink-0">
                    <PlantImage src={l.image} alt={l.name} ratio="1/1" className="rounded-lg" />
                  </div>
                  <div className="flex-1 text-sm">
                    <p className="font-medium">{l.name}</p>
                    <p className="text-ink-soft">Qty {l.qty}</p>
                  </div>
                  <span className="text-sm font-medium">{euro(l.price * l.qty)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 space-y-2 border-t border-line pt-4 text-sm">
              <Row label="Subtotal" value={euro(subtotal)} />
              <Row
                label={method === "collect" ? "Collection" : "Delivery"}
                value={
                  method === "collect"
                    ? "Free"
                    : deliveryFee == null
                    ? "Enter Eircode"
                    : deliveryFee === 0
                    ? "Free"
                    : euro(deliveryFee)
                }
              />
              <div className="flex items-center justify-between border-t border-line pt-3">
                <span className="font-semibold">Total</span>
                <span className="t-price text-xl">{euro(total)}</span>
              </div>
            </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
      {children}
    </label>
  );
}
function MethodCard({ active, icon: Icon, image, title, text, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`overflow-hidden rounded-2xl border text-left transition-colors ${
        active ? "border-forest-500 bg-forest-100" : "border-line hover:border-forest-500"
      }`}
    >
      <PlantImage src={image} alt="" ratio="5/2" />
      <span className="flex items-start gap-3 p-4">
        <span className={`mt-0.5 ${active ? "text-forest-700" : "text-ink-soft"}`}>
          <Icon size={22} />
        </span>
        <span>
          <span className="block font-display text-lg">{title}</span>
          <span className="block text-sm text-ink-soft">{text}</span>
        </span>
      </span>
    </button>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-soft">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
